import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
// import { baseURL } from "../backend_url";


import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Paper,
  IconButton,
  Divider,
  Chip,
  Tooltip,
  InputAdornment,
  Stack,
  Switch,
  Backdrop,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Place as PlaceIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  GridView as GridViewIcon,
  TableRows as TableRowsIcon,
  FilterAlt as FilterAltIcon,
  Sort as SortIcon,
  Star as StarIcon,
  LocalOffer as LocalOfferIcon,
  DirectionsCar as CarIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { motion, AnimatePresence } from "framer-motion";

// ---------- Helpers ----------
let baseURL = "https://caledontec.com/api"

const currencies = ["USD", "EUR", "GBP", "INR", "AED", "AUD", "CAD", "JPY", "SGD"];
const locales = [
  "en-us","en-gb","de-de","fr-fr","es-es","it-it","pt-br","hi-in"
];

const parsePrice = (s) => {
  if (!s) return null;
  const n = parseFloat(String(s).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

// ---------- Component ----------
export default function BookingComRates() {
  // Form
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const plus2 = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  }, []);

  const [pickup, setPickup] = useState("Mumbai");
  const [dropoff, setDropoff] = useState("Mumbai");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(plus2);
  const [age, setAge] = useState(30);
  const [currency, setCurrency] = useState("INR");
  const [locale, setLocale] = useState("en-us");
  const [max, setMax] = useState(60);

  // UI / data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]); // raw from API
  const [view, setView] = useState("grid"); // grid | table

  // Filters / sort
  const [qSupplier, setQSupplier] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("priceAsc"); // priceAsc | priceDesc | ratingDesc

  const priceNumbers = useMemo(
    () => rows.map((r) => parsePrice(r.price_text)).filter((n) => n != null),
    [rows]
  );
  const globalMin = useMemo(
    () => (priceNumbers.length ? Math.min(...priceNumbers) : null),
    [priceNumbers]
  );
  const globalMax = useMemo(
    () => (priceNumbers.length ? Math.max(...priceNumbers) : null),
    [priceNumbers]
  );

  const suppliers = useMemo(() => {
    const set = new Set(rows.filter(Boolean).map((r) => r.supplier).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  // Fetch
const fetchRates = async () => {
  setLoading(true);
  setError("");
  try {
    const base = new URL(baseURL);
    base.pathname = `${base.pathname.replace(/\/$/, "")}/booking_search`;
    // const url = new URL("/booking_search", baseURL);
    // url.searchParams.set("pickup", pickup);
    // url.searchParams.set("dropoff", dropoff || pickup);
    // url.searchParams.set("start", start);
    // url.searchParams.set("end", end);
    // url.searchParams.set("age", String(age));
    // url.searchParams.set("currency", currency);
    // url.searchParams.set("locale", locale);
    // url.searchParams.set("max", String(max));
    // const resp = await axios.get(base.toString(), {
    //   params: {
    //     pickup,
    //     dropoff: dropoff || pickup,
    //     start,
    //     end,
    //     age: String(age),
    //     currency,
    //     locale,
    //     max: String(max),
    //   },
    //   timeout: 20000,
    //   withCredentials: false,
    //   validateStatus: () => true,
    // });
    const resp = await axios.post(
      base.toString(),
      {
        pickup,
        dropoff: dropoff || pickup,
        start,
        end,
        age: String(age),
        currency,
        locale,
        max: String(max),
      },
      {
        timeout: 20000,
        withCredentials: false,
        validateStatus: () => true,
        headers: { "Content-Type": "application/json" },
      }
    );

    // const resp = await axios.get(base.toString(), {
    // // const resp = await axios.get(url.toString(), {
    //   timeout: 20000, // 20s safety
    //   withCredentials: false,
    //   validateStatus: () => true, // let us handle HTTP codes ourselves
    // });

    if (!resp || typeof resp.status !== "number") {
      throw new Error("Network error: no response from server");
    }
    if (resp.status < 200 || resp.status >= 300) {
      throw new Error(
        `HTTP ${resp.status}: ${
          resp.data?.error || resp.statusText || "Request failed"
        }`
      );
    }

    const data = resp.data?.results || [];
    setRows(Array.isArray(data) ? data : []);
  } catch (e) {
    const msg = String(e?.message || e);
    if (/Network Error/i.test(msg)) {
      setError("Network error (likely CORS/SSL/preflight issue)");
    } else if (/timeout/i.test(msg)) {
      setError("Request timed out — try again or reduce 'max' results");
    } else {
      setError(msg);
    }
    setRows([]);
  } finally {
    setLoading(false);
  }
};

  // Derived filtered/sorted rows
  const visibleRows = useMemo(() => {
    let arr = rows.slice();

    // supplier filter (substring)
    if (qSupplier.trim()) {
      const q = qSupplier.toLowerCase();
      arr = arr.filter((r) => (r.supplier || "").toLowerCase().includes(q));
    }

    // price range
    const minN = minPrice !== "" ? Number(minPrice) : null;
    const maxN = maxPrice !== "" ? Number(maxPrice) : null;
    arr = arr.filter((r) => {
      const n = parsePrice(r.price_text);
      if (n == null) return true; // keep unknowns
      if (minN != null && n < minN) return false;
      if (maxN != null && n > maxN) return false;
      return true;
    });

    // sort
    arr.sort((a, b) => {
      const pa = parsePrice(a.price_text);
      const pb = parsePrice(b.price_text);
      const ra = parseFloat(a.rating_text) || 0;
      const rb = parseFloat(b.rating_text) || 0;

      if (sortBy === "priceAsc") {
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return pa - pb;
      }
      if (sortBy === "priceDesc") {
        if (pa == null && pb == null) return 0;
        if (pa == null) return 1;
        if (pb == null) return -1;
        return pb - pa;
      }
      // ratingDesc
      return rb - ra;
    });

    return arr;
  }, [rows, qSupplier, minPrice, maxPrice, sortBy]);

  const resetForm = () => {
    setPickup("");
    setDropoff("");
    setStart(today);
    setEnd(plus2);
    setAge(30);
    setCurrency("INR");
    setLocale("en-us");
    setMax(60);
    setRows([]);
    setQSupplier("");
    setMinPrice("");
    setMaxPrice("");
    setSortBy("priceAsc");
  };

  // ---------- UI Pieces ----------
  const SearchPanel = (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Booking.com Cars — Search</Typography>
        <Box component="form" onSubmit={(e)=>{e.preventDefault(); fetchRates();}}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Pickup"
                fullWidth
                value={pickup}
                onChange={(e) => setPickup(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Dropoff"
                fullWidth
                value={dropoff}
                onChange={(e) => setDropoff(e.target.value)}
                placeholder="Same as pickup"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Start"
                type="date"
                fullWidth
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="End"
                type="date"
                fullWidth
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Driver Age"
                type="number"
                fullWidth
                inputProps={{ min: 18, max: 100 }}
                value={age}
                onChange={(e) => setAge(parseInt(e.target.value || "0", 10))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={6} md={2}>
              <TextField
                select
                label="Currency"
                fullWidth
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                size="small"
              >
                {currencies.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                label="Locale"
                fullWidth
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                size="small"
              >
                {locales.map((l) => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Max Results"
                type="number"
                fullWidth
                value={max}
                onChange={(e) => setMax(parseInt(e.target.value || "1", 10))}
                size="small"
                inputProps={{ min: 1, max: 200 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LoadingButton
                fullWidth
                variant="contained"
                onClick={fetchRates}
                loading={loading}
                startIcon={<SearchIcon />}
              >
                Search
              </LoadingButton>
            </Grid>
            <Grid item xs={12} md={3}>
              <Tooltip title="Reset all">
                <span>
                  <LoadingButton
                    fullWidth
                    color="inherit"
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    startIcon={<RefreshIcon />}
                  >
                    Reset
                  </LoadingButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );

  const ControlsBar = (
    <Paper elevation={1} sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
      <TextField
        size="small"
        placeholder="Filter suppliers…"
        value={qSupplier}
        onChange={(e) => setQSupplier(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
      />
      <TextField
        size="small"
        label="Min price"
        type="number"
        value={minPrice}
        onChange={(e) => setMinPrice(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><LocalOfferIcon /></InputAdornment> }}
      />
      <TextField
        size="small"
        label="Max price"
        type="number"
        value={maxPrice}
        onChange={(e) => setMaxPrice(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><LocalOfferIcon /></InputAdornment> }}
      />
      <TextField
        select
        size="small"
        label="Sort"
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SortIcon /></InputAdornment> }}
      >
        <MenuItem value="priceAsc">Price ↑</MenuItem>
        <MenuItem value="priceDesc">Price ↓</MenuItem>
        <MenuItem value="ratingDesc">Rating ↓</MenuItem>
      </TextField>

      <Box sx={{ flex: 1 }} />

      <ToggleButtonGroup
        size="small"
        exclusive
        value={view}
        onChange={(_, v) => v && setView(v)}
      >
        <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
        <ToggleButton value="table"><TableRowsIcon fontSize="small" /></ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );

  const GridCard = ({ item }) => {
    const price = parsePrice(item.price_text);
    const isCheapest = globalMin != null && price === globalMin;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
      >
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Box sx={{ aspectRatio: "16/9", bgcolor: "grey.100", borderRadius: 2, overflow: "hidden", mb: 2, display: "grid", placeItems: "center" }}>
              {item.image ? (
                <img src={item.image} alt={item.title || "Car"} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <CarIcon color="disabled" fontSize="large" />
              )}
            </Box>

            <Stack spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>{item.title || "Car"}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={item.supplier || "—"} />
                {item.rating_text && (
                  <Chip
                    size="small"
                    icon={<StarIcon sx={{ fontSize: 16 }} />}
                    label={item.rating_text}
                    color="warning"
                    variant="outlined"
                  />
                )}
                {item.free_cancellation && (
                  <Chip size="small" label="Free cancellation" color="success" variant="outlined" />
                )}
              </Stack>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {item.price_text || "—"}
                {isCheapest && (
                  <Chip size="small" color="success" sx={{ ml: 1 }} label="Cheapest" />
                )}
              </Typography>

              {item.details_url && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                  <LinkIcon fontSize="small" color="primary" />
                  <a href={item.details_url} target="_blank" rel="noreferrer">View details</a>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  const TableView = () => (
    <Paper elevation={1} sx={{ overflow: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr style={{ background: "#f7f7f7" }}>
            <th style={thSticky}>Supplier</th>
            <th style={th}>Title</th>
            <th style={th}>Price</th>
            <th style={th}>Rating</th>
            <th style={th}>Free Cancellation</th>
            <th style={th}>Link</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => (
            <tr key={i} style={{ borderTop: "1px solid #eee" }}>
              <td style={tdSticky}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={r.supplier || "—"} />
                </Stack>
              </td>
              <td style={td}>{r.title || "—"}</td>
              <td style={td}><strong>{r.price_text || "—"}</strong></td>
              <td style={td}>{r.rating_text ? <><StarIcon sx={{ fontSize: 16, verticalAlign: "text-bottom", color: "#F5A524" }} /> {r.rating_text}</> : "—"}</td>
              <td style={td}>{r.free_cancellation ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" label="No" />}</td>
              <td style={td}>
                {r.details_url ? (
                  <a href={r.details_url} target="_blank" rel="noreferrer">Open</a>
                ) : "—"}
              </td>
            </tr>
          ))}
          {visibleRows.length === 0 && (
            <tr><td style={{ padding: 16 }} colSpan={6}><Typography color="text.secondary">No results.</Typography></td></tr>
          )}
        </tbody>
      </table>
    </Paper>
  );

  // styles for “sticky supplier” column in the table view
  const thSticky = {
    position: "sticky", left: 0, top: 0, zIndex: 3, padding: 12, textAlign: "left",
    borderRight: "1px solid #eee", background: "#f7f7f7", minWidth: 180
  };
  const tdSticky = {
    position: "sticky", left: 0, padding: 12, borderRight: "1px solid #eee",
    background: "#fafafa", minWidth: 180
  };
  const th = { padding: 12, textAlign: "left" };
  const td = { padding: 12 };

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      {SearchPanel}

      {/* Secondary controls (filters/sort) */}
      {ControlsBar}

      {/* Summary */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Chip label={`Results: ${visibleRows.length}`} />
        {globalMin != null && <Chip color="success" variant="outlined" label={`Min: ${globalMin}`} />}
        {globalMax != null && <Chip color="error" variant="outlined" label={`Max: ${globalMax}`} />}
        <Chip variant="outlined" label={`API: ${baseURL}`} />
      </Stack>

      {/* Results */}
      {view === "grid" ? (
        <Grid container spacing={2}>
          <AnimatePresence>
            {visibleRows.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <GridCard item={item} />
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      ) : (
        <TableView />
      )}

      {/* Loader */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, color: "#fff", flexDirection: "column", gap: 2 }}
      >
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "center" }}>
            Fetching Booking.com car rates…
          </Typography>
        </motion.div>
        <Box sx={{ width: { xs: "80%", sm: "60%", md: "40%" } }}>
          <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
          <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
          <LinearProgress sx={{ borderRadius: 1 }} />
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          Rendering results and ranking by price…
        </Typography>
      </Backdrop>
    </Box>
  );
}
